import React from 'react';

import { Page, useExecutiveCheckupRouter } from '../../router/context';
import { saveSchedule } from '../../../../modules/slots';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { createScheduleRequest } from '../../../../models/ScheduleRequest';
import { usePatientContext } from '../../../../contexts/PatientContext';
import { AppointmentConfirmationTemplate } from '../../../../templates/AppointmentConfirmationTemplate';
import TagInfo from '../../../../components/TagInfo';
import { ScheduleCard } from './components/ScheduleCard';
import { SystemUnavailableError } from '../../../../errors';
import { SlotUnavailableError } from '../../../../errors/SlotUnavailableError';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { useScheduledItems } from '../../store/ScheduledItems';
import { CallCenterModal } from '../../../../components/CallCenterModal';
import { MedicalRecorderError } from '../../../../errors/MedicalRecorderError';

const WarningBlueIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/warningBlue.svg';

type AppointmentConfirmationListProps = {
  isFirstAppointment?: boolean;
};

export function AppointmentConfirmationList({ isFirstAppointment }: AppointmentConfirmationListProps) {
  const router = useExecutiveCheckupRouter();
  const { scheduleItems } = useScheduleContext();
  const { patientSelected } = usePatientContext();
  const { selectedService } = useServiceContext();

  const { scheduledItems, setScheduledItems } = useScheduledItems();

  const [loading, setLoading] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);
  const [isCallCenterModalVisible, setIsCallCenterModalVisible] = React.useState(false);

  const handleError = (error: Error) => {
    if (error instanceof MedicalRecorderError) setIsCallCenterModalVisible(true);

    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);

    if (error instanceof SlotUnavailableError) setIsModalSlotUnavailableVisible(true);
  };

  const schedules = React.useMemo(
    () => scheduleItems.map(item => ({ ...item, productName: `Check-up Executivo - ${item.name}` })),
    [scheduleItems]
  );

  const saveSchedules = async () => {
    setLoading(true);
    if (patientSelected) {
      const schedule = createScheduleRequest(patientSelected, schedules, [], isFirstAppointment, selectedService.id);

      try {
        const { token } = await saveSchedule(schedule);

        if (token) {
          let dateScheduled = '';
          schedule.schedules.forEach(item => {
            if (item.selectedSlotOptions?.[0])
              dateScheduled = `${item.selectedSlotOptions[0].date}T${item.selectedSlotOptions[0].hour}`;
          });

          if (dateScheduled.length) setScheduledItems([...scheduledItems, dateScheduled]);
          router.navigate(Page.CONCLUSION, { isFirstAppointment, productId: schedule.schedules[0]?.productId, token });
        }
      } catch (error: any) {
        console.log('Error trying to schedule/reschedule', error);

        handleError(error);
      }
    }
    setLoading(false);
  };

  return (
    <>
      <AppointmentConfirmationTemplate handleContinue={saveSchedules} loading={loading} goBack={() => router.goBack()}>
        {scheduleItems.map(schedule => (
          <>
            <ScheduleCard schedule={schedule} key={schedule.id} isFirstAppointment={!!isFirstAppointment} />
            {schedule.careType === 'PRESENTIAL' && (
              <div style={{ marginTop: 24 }}>
                <TagInfo
                  info="É recomendado que você chegue na unidade com 15 minutos de antecedência."
                  icon={
                    <img
                      src={WarningBlueIcon}
                      alt="É recomendado que você chegue na unidade com 15 minutos de antecedência."
                    />
                  }
                />
              </div>
            )}
          </>
        ))}
      </AppointmentConfirmationTemplate>

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />

      <CallCenterModal
        visible={isCallCenterModalVisible}
        onClose={() => setIsCallCenterModalVisible(false)}
        serviceId={selectedService.id || ''}
        serviceName={selectedService.name || ''}
        productId={schedules[0].productId}
        productName={schedules[0].productName || ''}
        unitId={schedules[0].unit?.unitId || ''}
        unitName={schedules[0]?.unit?.name || ''}
        textMessage={''}
      />
    </>
  );
}
